import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Upload, Check, ChevronRight, Send, Image as ImageIcon, Trash2, Sparkles, Star } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { submitApp, submitAppUpload } from "@/lib/api";

const REGIONS = ["Worldwide", "Europe", "USA", "Australia", "Asia", "Africa", "South America"];
const AUDIENCES = ["owners", "riders", "trainers", "breeders", "stable_managers", "vets", "farriers"];

const EMPTY = {
  name: "", tagline: "", description: "",
  logo: "", website: "", founded: 2026, country: "",
  languages: ["EN"],
  platforms: { ios: false, android: false, web: false },
  pricing: { free_tier: false, premium: false, monthly_usd: 0, yearly_usd: 0 },
  regions: [], target_audience: [], rating: 0, review_count: 0,
  features: [], color: "#2563EB",
};

const EMPTY_BILLING = {
  company: "", vat_number: "", address: "", city: "", postal_code: "", country: "",
};

const TIERS = [
  {
    id: "standard",
    nameEn: "Standard listing", nameNl: "Standaardvermelding",
    priceEn: "Free", priceNl: "Gratis",
    blurbEn: "Editor reviews & publishes within 2-3 business days. Full directory inclusion.",
    blurbNl: "Onze redactie controleert en publiceert binnen 2-3 werkdagen. Volledige opname in de gids.",
    perksEn: ["Listed in all 22 languages", "Top 100 ranking eligible", "Auto-included in comparisons & versus pages"],
    perksNl: ["Vermelding in alle 22 talen", "Komt in aanmerking voor Top 100", "Automatisch in vergelijkingen & versus-pagina's"],
  },
  {
    id: "premium_monthly",
    nameEn: "Premium · monthly", nameNl: "Premium · per maand",
    priceEn: "€99 / month", priceNl: "€99 / maand",
    blurbEn: "Everything in Standard, plus instant publish, score boost and gold badge.",
    blurbNl: "Alles in Standaard, plus instant publish, score-boost en gouden badge.",
    perksEn: [
      "Instant publish (within 24h after invoice paid)",
      "Score boost — your app ranks higher in directory + Top 100",
      "Pinned in TrendingStrip on the homepage",
      "Gold Premium badge on detail + listing cards",
      "Cancel anytime",
    ],
    perksNl: [
      "Instant publish (binnen 24u na betaling factuur)",
      "Score-boost — je app ranked hoger in directory + Top 100",
      "Gepind in TrendingStrip op de homepage",
      "Gouden Premium badge op detail + listing-kaarten",
      "Per maand opzegbaar",
    ],
    highlight: true,
  },
  {
    id: "premium_yearly",
    nameEn: "Premium · yearly", nameNl: "Premium · jaarlijks",
    priceEn: "€990 / year (2 months free)", priceNl: "€990 / jaar (2 maanden gratis)",
    blurbEn: "Best value — same perks as monthly, with 2 months free.",
    blurbNl: "Beste waarde — dezelfde voordelen als maandelijks, met 2 maanden gratis.",
    perksEn: [
      "Everything in Premium monthly",
      "2 months free vs. monthly billing",
      "Priority editorial support",
    ],
    perksNl: [
      "Alles in Premium per maand",
      "2 maanden gratis t.o.v. maandelijks",
      "Voorrang op redactionele support",
    ],
  },
];

const SubmitAppPage = () => {
  const { lang } = useLang();
  const isNl = lang === "nl";
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(EMPTY);
  const [submitter, setSubmitter] = useState({ name: "", email: "", role: "founder", notes: "" });
  const initialTier = (() => {
    const q = searchParams.get("tier");
    return ["standard", "premium_monthly", "premium_yearly"].includes(q) ? q : "standard";
  })();
  const [tier, setTier] = useState(initialTier);
  const [billing, setBilling] = useState(EMPTY_BILLING);
  const [taxonomy, setTaxonomy] = useState({});
  const [screenshots, setScreenshots] = useState([]); // [{url, name}]
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [doneTier, setDoneTier] = useState("standard");
  const [discount, setDiscount] = useState({ code: "", status: "idle", percent_off: 0, description: "" });

  // Pre-fill discount from ?code= query param (used in newsletter CTAs)
  useEffect(() => {
    const c = searchParams.get("code");
    if (c) setDiscount((d) => ({ ...d, code: c.toUpperCase() }));
  }, [searchParams]);

  const checkDiscount = async () => {
    const code = (discount.code || "").trim().toUpperCase();
    if (!code) { setDiscount({ code: "", status: "idle", percent_off: 0, description: "" }); return; }
    setDiscount((d) => ({ ...d, code, status: "checking" }));
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/discount-codes/${encodeURIComponent(code)}`);
      if (!r.ok) throw new Error("invalid");
      const data = await r.json();
      setDiscount({ code: data.code, status: "valid", percent_off: data.percent_off, description: data.description || "" });
    } catch (e) {
      setDiscount({ code, status: "invalid", percent_off: 0, description: "" });
    }
  };

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/meta/features`)
      .then((r) => r.json()).then((d) => setTaxonomy(d.categories || d)).catch(() => setTaxonomy({}));
    document.title = isNl ? "Meld je paardenapp aan — HorseAppCompare" : "List your horse app — HorseAppCompare";
  }, [isNl]);

  const toggleArr = (key, val) =>
    setForm((f) => ({ ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val] }));

  const uploadImage = async (file, target) => {
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await submitAppUpload(file);
      const abs = url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
      if (target === "logo") setForm((f) => ({ ...f, logo: abs }));
      else setScreenshots((s) => [...s, { url, displayUrl: abs, name: file.name }]);
    } catch (err) { alert(`Upload failed: ${err.response?.data?.detail || err.message}`); }
    finally { setUploading(false); }
  };

  const removeScreenshot = (idx) => setScreenshots((s) => s.filter((_, i) => i !== idx));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!submitter.email || !form.name) { alert(isNl ? "Vul je app-naam en je e-mailadres in." : "Please fill app name and your email."); return; }
    if (tier !== "standard") {
      if (!billing.company || !billing.address || !billing.country) {
        alert(isNl ? "Voor Premium hebben we factuurgegevens nodig (bedrijf, adres, land)." : "Premium requires billing details (company, address, country).");
        return;
      }
    }
    setSubmitting(true);
    try {
      await submitApp({
        app: form,
        submitter_name: submitter.name,
        submitter_email: submitter.email,
        submitter_role: submitter.role,
        notes: submitter.notes,
        screenshot_urls: screenshots.map((s) => s.url),
        tier,
        billing: tier !== "standard" ? billing : null,
        discount_code: discount.status === "valid" ? discount.code : "",
      });
      setDoneTier(tier);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) { alert(`Submission failed: ${err.response?.data?.detail || err.message}`); }
    finally { setSubmitting(false); }
  };

  if (done) {
    const isPremium = doneTier !== "standard";
    return (
      <main className="hac-container py-20 max-w-2xl text-center" data-testid="submit-success">
        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-6 ${isPremium ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
          <Check size={28} />
        </div>
        <h1 className="font-heading text-4xl text-[#0F172A] mb-3">
          {isPremium
            ? (isNl ? "Bedankt voor je Premium aanmelding!" : "Thanks for going Premium!")
            : (isNl ? "Bedankt! Je app is ingediend." : "Thanks! Your app has been submitted.")}
        </h1>
        <p className="text-[#64748B] leading-relaxed mb-6">
          {isPremium
            ? (isNl
              ? "We sturen je binnen 1 werkdag een factuur op het opgegeven e-mailadres. Zodra de betaling binnen is, gaat je app instant live met alle Premium-voordelen — instant publish, score-boost, gouden badge en pinned in TrendingStrip."
              : "We'll email you an invoice within 1 business day. Once the payment lands, your app goes live instantly with all Premium perks — instant publish, score boost, gold badge and TrendingStrip pin.")
            : (isNl
              ? "Onze redactie beoordeelt je inzending. We nemen binnen 2-3 werkdagen contact op via het opgegeven e-mailadres."
              : "Our editorial team will review your submission. We'll reach out within 2-3 business days at the email you provided.")}
        </p>
        {isPremium && (
          <p className="text-xs text-[#94A3B8] mb-8">
            {isNl ? "Vragen? Mail" : "Questions? Email"} <a href="mailto:support@horseappcompare.com" className="text-[#2563EB] hover:underline">support@horseappcompare.com</a>
          </p>
        )}
        <Link to="/" className="hac-btn-primary"><ChevronRight size={14} /> {isNl ? "Terug naar home" : "Back to home"}</Link>
      </main>
    );
  }

  return (
    <main data-testid="submit-app-page">
      <section className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
        <div className="hac-container py-16 max-w-4xl">
          <nav className="flex items-center gap-2 text-xs text-[#64748B] mb-4">
            <Link to="/" className="hover:text-[#2563EB]">Home</Link>
            <ChevronRight size={12} />
            <span className="text-[#0F172A] font-medium">{isNl ? "Meld je app aan" : "List your app"}</span>
          </nav>
          <div className="overline mb-3 text-[#2563EB]"><Sparkles size={12} className="inline mr-1" /> {isNl ? "Voor app-eigenaren" : "For app owners"}</div>
          <h1 className="font-heading text-4xl md:text-6xl text-[#0F172A] leading-[1.05] mb-4">
            {isNl ? "Voeg je paardenapp toe aan de gids" : "List your horse app on HorseAppCompare"}
          </h1>
          <p className="text-lg text-[#64748B] leading-relaxed max-w-2xl">
            {isNl
              ? "Vul het formulier in, upload je logo en screenshots, en onze redactie zet je app live binnen 2-3 werkdagen. Volledig gratis voor de standaardlisting — je app verschijnt automatisch in 22 talen, in de Top 100, in vergelijkingen en in de SEO-sitemap."
              : "Fill in the form, upload your logo and screenshots — our editors review it and publish within 2-3 business days. Standard listings are free; your app appears automatically in 22 languages, the Top 100, comparisons and the SEO sitemap."}
          </p>
        </div>
      </section>

      <form onSubmit={onSubmit} className="hac-container py-12 max-w-3xl space-y-10">
        {/* Step 0: Tier picker */}
        <section className="bg-white border border-[#E2E8F0] rounded-2xl p-6" data-testid="tier-picker">
          <div className="flex items-center justify-between mb-1">
            <h2 className="font-bold text-xl text-[#0F172A]">0. {isNl ? "Kies je vermeldingstype" : "Choose your listing type"}</h2>
            <span className="text-[10px] uppercase tracking-widest text-[#94A3B8]">{isNl ? "Optioneel upgraden" : "Optional upgrade"}</span>
          </div>
          <p className="text-sm text-[#64748B] mb-5">{isNl ? "Standaardvermelding is volledig gratis. Premium versnelt publicatie en boost je positie." : "Standard listing is completely free. Premium speeds up publishing and boosts your ranking."}</p>
          <div className="grid md:grid-cols-3 gap-3">
            {TIERS.map((tt) => {
              const selected = tier === tt.id;
              return (
                <button key={tt.id} type="button" onClick={() => setTier(tt.id)}
                  className={`text-left p-5 rounded-xl border-2 transition-all relative ${selected ? "border-[#2563EB] bg-[#EFF6FF] shadow-sm" : "border-[#E2E8F0] bg-white hover:border-[#94A3B8]"}`}
                  data-testid={`tier-${tt.id}`}>
                  {tt.highlight && (
                    <span className="absolute -top-2 -right-2 bg-amber-400 text-[10px] text-amber-900 font-bold uppercase px-2 py-0.5 rounded-full">{isNl ? "Populair" : "Popular"}</span>
                  )}
                  <div className="flex items-center gap-2 mb-1">
                    {tt.id !== "standard" && <Star size={14} className="text-amber-500 fill-amber-500" />}
                    <div className="font-bold text-[#0F172A]">{isNl ? tt.nameNl : tt.nameEn}</div>
                  </div>
                  <div className="text-2xl font-bold text-[#2563EB] mb-2">{isNl ? tt.priceNl : tt.priceEn}</div>
                  <p className="text-xs text-[#64748B] mb-3">{isNl ? tt.blurbNl : tt.blurbEn}</p>
                  <ul className="space-y-1">
                    {(isNl ? tt.perksNl : tt.perksEn).map((p, i) => (
                      <li key={i} className="text-[11px] text-[#0F172A] flex items-start gap-1.5">
                        <Check size={11} className="text-[#16A34A] mt-0.5 shrink-0" /> {p}
                      </li>
                    ))}
                  </ul>
                </button>
              );
            })}
          </div>
          {tier !== "standard" && (
            <div className="mt-5 p-4 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
              <strong>{isNl ? "Hoe werkt betaling?" : "How does payment work?"}</strong> {isNl
                ? "We sturen je een factuur per e-mail binnen 1 werkdag. Na betaling activeren we Premium en gaat je app direct live. Geen Stripe-account nodig — bankoverschrijving of iDEAL volstaat."
                : "We email you an invoice within 1 business day. Once paid we activate Premium and your app goes live immediately. No Stripe account needed — bank transfer or iDEAL is fine."}
            </div>
          )}
        </section>

        {/* Discount code — only when premium */}
        {tier !== "standard" && (
          <section className="bg-white border border-emerald-200 rounded-2xl p-6" data-testid="discount-section">
            <h2 className="font-bold text-lg text-[#0F172A] mb-1 flex items-center gap-2">
              🎟️ {isNl ? "Kortingscode (optioneel)" : "Discount code (optional)"}
            </h2>
            <p className="text-sm text-[#64748B] mb-3">{isNl ? "Heb je een kortingscode uit onze nieuwsbrief of email? Voer hem hier in." : "Got a discount code from our newsletter or email? Enter it here."}</p>
            <div className="flex gap-2 max-w-md">
              <input
                className="hac-input uppercase font-mono"
                value={discount.code}
                onChange={(e) => setDiscount({ code: e.target.value.toUpperCase().slice(0, 32), status: "idle", percent_off: 0, description: "" })}
                placeholder="WELCOME10"
                data-testid="discount-input"
                maxLength={32}
              />
              <button type="button" onClick={checkDiscount} disabled={!discount.code || discount.status === "checking"} className="hac-btn-secondary text-sm whitespace-nowrap disabled:opacity-60" data-testid="discount-apply">
                {discount.status === "checking" ? "..." : (isNl ? "Toepassen" : "Apply")}
              </button>
            </div>
            {discount.status === "valid" && (() => {
              const base = tier === "premium_yearly" ? 990 : tier === "premium_monthly" ? 99 : 0;
              const discounted = Math.round(base * (100 - discount.percent_off)) / 100;
              const period = tier === "premium_yearly" ? (isNl ? "/jaar" : "/year") : (isNl ? "/maand" : "/month");
              return (
                <div className="mt-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-start gap-2" data-testid="discount-valid">
                  <Check size={16} className="shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <strong>{discount.percent_off}% {isNl ? "korting toegepast" : "discount applied"}</strong> ({discount.code})
                    {discount.description && <div className="text-xs text-emerald-600 mt-0.5">{discount.description}</div>}
                    {base > 0 && (
                      <div className="mt-2 inline-flex items-baseline gap-2 bg-white border border-emerald-300 rounded-lg px-3 py-1.5" data-testid="discount-new-price">
                        <span className="text-xs line-through text-[#94A3B8]">€{base}{period}</span>
                        <span className="text-base font-bold text-emerald-700">€{discounted}{period}</span>
                      </div>
                    )}
                    <div className="text-xs text-emerald-600 mt-1">{isNl ? "De korting wordt verrekend op de factuur." : "The discount will be applied on the invoice."}</div>
                  </div>
                </div>
              );
            })()}
            {discount.status === "invalid" && (
              <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="discount-invalid">
                ❌ {isNl ? "Code ongeldig of verlopen." : "Code invalid or expired."}
              </div>
            )}
          </section>
        )}

        {/* Billing details — only when premium */}
        {tier !== "standard" && (
          <section className="bg-white border border-amber-200 rounded-2xl p-6" data-testid="billing-section">
            <h2 className="font-bold text-xl text-[#0F172A] mb-1 flex items-center gap-2">
              <Star size={16} className="text-amber-500 fill-amber-500" />
              {isNl ? "Factuurgegevens" : "Billing details"}
            </h2>
            <p className="text-sm text-[#64748B] mb-5">{isNl ? "We sturen de factuur naar het e-mailadres uit stap 1 en gebruiken deze gegevens op de factuur." : "We send the invoice to the email from step 1 and use these details on the invoice."}</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="overline block mb-1 text-[#64748B]">{isNl ? "Bedrijfsnaam *" : "Company name *"}</label>
                <input required className="hac-input" value={billing.company} onChange={(e) => setBilling({ ...billing, company: e.target.value })} data-testid="billing-company" />
              </div>
              <div>
                <label className="overline block mb-1 text-[#64748B]">{isNl ? "BTW-nummer (optioneel)" : "VAT number (optional)"}</label>
                <input className="hac-input" value={billing.vat_number} onChange={(e) => setBilling({ ...billing, vat_number: e.target.value })} placeholder="NL000000000B00" data-testid="billing-vat" />
              </div>
              <div>
                <label className="overline block mb-1 text-[#64748B]">{isNl ? "Land *" : "Country *"}</label>
                <input required className="hac-input" value={billing.country} onChange={(e) => setBilling({ ...billing, country: e.target.value })} data-testid="billing-country" />
              </div>
              <div className="sm:col-span-2">
                <label className="overline block mb-1 text-[#64748B]">{isNl ? "Straat + huisnummer *" : "Street + number *"}</label>
                <input required className="hac-input" value={billing.address} onChange={(e) => setBilling({ ...billing, address: e.target.value })} data-testid="billing-address" />
              </div>
              <div>
                <label className="overline block mb-1 text-[#64748B]">{isNl ? "Postcode" : "Postal code"}</label>
                <input className="hac-input" value={billing.postal_code} onChange={(e) => setBilling({ ...billing, postal_code: e.target.value })} data-testid="billing-postal" />
              </div>
              <div>
                <label className="overline block mb-1 text-[#64748B]">{isNl ? "Plaats" : "City"}</label>
                <input className="hac-input" value={billing.city} onChange={(e) => setBilling({ ...billing, city: e.target.value })} data-testid="billing-city" />
              </div>
            </div>
          </section>
        )}

        {/* Step 1: Your contact */}
        <section className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
          <h2 className="font-bold text-xl text-[#0F172A] mb-1">1. {isNl ? "Jouw contactgegevens" : "Your contact"}</h2>
          <p className="text-sm text-[#64748B] mb-5">{isNl ? "We gebruiken dit alleen om met je af te stemmen — niet zichtbaar op de site." : "We only use this to coordinate the publish — not shown publicly."}</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="overline block mb-1 text-[#64748B]">{isNl ? "Volledige naam" : "Full name"}</label>
              <input className="hac-input" value={submitter.name} onChange={(e) => setSubmitter({ ...submitter, name: e.target.value })} data-testid="submit-name" />
            </div>
            <div>
              <label className="overline block mb-1 text-[#64748B]">E-mail *</label>
              <input type="email" required className="hac-input" value={submitter.email} onChange={(e) => setSubmitter({ ...submitter, email: e.target.value })} data-testid="submit-email" />
            </div>
            <div>
              <label className="overline block mb-1 text-[#64748B]">{isNl ? "Jouw rol" : "Your role"}</label>
              <select className="hac-input" value={submitter.role} onChange={(e) => setSubmitter({ ...submitter, role: e.target.value })} data-testid="submit-role">
                <option value="founder">{isNl ? "Oprichter / eigenaar" : "Founder / owner"}</option>
                <option value="marketing">Marketing / PR</option>
                <option value="developer">Developer</option>
                <option value="other">{isNl ? "Anders" : "Other"}</option>
              </select>
            </div>
          </div>
        </section>

        {/* Step 2: App basics */}
        <section className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
          <h2 className="font-bold text-xl text-[#0F172A] mb-5">2. {isNl ? "Basisgegevens app" : "App basics"}</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="overline block mb-1 text-[#64748B]">{isNl ? "App-naam" : "App name"} *</label>
              <input required className="hac-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="submit-app-name" />
            </div>
            <div>
              <label className="overline block mb-1 text-[#64748B]">Tagline</label>
              <input className="hac-input" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="One-line pitch (English)" data-testid="submit-tagline-en" />
              <p className="text-[11px] text-[#94A3B8] mt-1">{isNl ? "In het Engels — we vertalen automatisch naar 22 talen." : "In English — we auto-translate to 22 languages."}</p>
            </div>
            <div className="sm:col-span-2">
              <label className="overline block mb-1 text-[#64748B]">{isNl ? "Beschrijving" : "Description"}</label>
              <textarea className="hac-input min-h-[100px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What does your app do? Who is it for? (English)" data-testid="submit-desc-en" />
            </div>
            <div>
              <label className="overline block mb-1 text-[#64748B]">Website</label>
              <input className="hac-input" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://" data-testid="submit-website" />
            </div>
            <div>
              <label className="overline block mb-1 text-[#64748B]">{isNl ? "Land" : "Country"}</label>
              <input className="hac-input" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="submit-country" />
            </div>
            <div>
              <label className="overline block mb-1 text-[#64748B]">{isNl ? "Opgericht" : "Founded"}</label>
              <input type="number" className="hac-input" value={form.founded} onChange={(e) => setForm({ ...form, founded: parseInt(e.target.value || "0") })} data-testid="submit-founded" />
            </div>
            <div>
              <label className="overline block mb-1 text-[#64748B]">{isNl ? "Accent kleur" : "Brand colour"}</label>
              <input type="color" className="hac-input h-11" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} data-testid="submit-color" />
            </div>
          </div>
        </section>

        {/* Step 3: Logo + screenshots */}
        <section className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
          <h2 className="font-bold text-xl text-[#0F172A] mb-1">3. {isNl ? "Logo & screenshots" : "Logo & screenshots"}</h2>
          <p className="text-sm text-[#64748B] mb-5">{isNl ? "PNG/JPG/WebP, max 8 MB per bestand." : "PNG/JPG/WebP, max 8 MB per file."}</p>

          <label className="overline block mb-2 text-[#64748B]">{isNl ? "Logo (vierkant aanbevolen)" : "Logo (square recommended)"}</label>
          {form.logo ? (
            <div className="flex items-center gap-3 mb-5 p-3 border border-[#E2E8F0] rounded-lg bg-[#F8FAFC]">
              <img src={form.logo} alt="logo" className="w-16 h-16 rounded-lg object-cover" />
              <div className="flex-1 text-xs text-[#64748B] truncate">{form.logo}</div>
              <button type="button" onClick={() => setForm({ ...form, logo: "" })} className="text-xs text-[#EF4444] hover:underline">Remove</button>
            </div>
          ) : (
            <label className="hac-input mb-5 flex items-center justify-center gap-2 cursor-pointer text-[#64748B] hover:border-[#2563EB] hover:text-[#2563EB]">
              <Upload size={14} /> {uploading ? "Uploading…" : (isNl ? "Klik om logo te uploaden" : "Click to upload logo")}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0], "logo")} data-testid="submit-logo-upload" />
            </label>
          )}

          <label className="overline block mb-2 text-[#64748B]">{isNl ? "Screenshots" : "Screenshots"} ({screenshots.length})</label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
            {screenshots.map((s, i) => (
              <div key={i} className="relative group">
                <img src={s.displayUrl} alt="" className="w-full h-32 object-cover rounded-lg border border-[#E2E8F0]" />
                <button type="button" onClick={() => removeScreenshot(i)} className="absolute top-1 right-1 bg-white/90 text-[#EF4444] p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`submit-screenshot-remove-${i}`}><Trash2 size={12} /></button>
              </div>
            ))}
            <label className="h-32 flex flex-col items-center justify-center gap-1 border-2 border-dashed border-[#E2E8F0] rounded-lg cursor-pointer text-[#64748B] hover:border-[#2563EB] hover:text-[#2563EB]">
              <ImageIcon size={20} />
              <span className="text-xs">{uploading ? "Uploading…" : (isNl ? "Voeg toe" : "Add")}</span>
              <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0], "screenshot")} data-testid="submit-screenshot-upload" />
            </label>
          </div>
        </section>

        {/* Step 4: Platforms + regions + audiences */}
        <section className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-6">
          <h2 className="font-bold text-xl text-[#0F172A]">4. {isNl ? "Beschikbaarheid" : "Availability"}</h2>
          <div>
            <label className="overline block mb-2 text-[#64748B]">Platforms</label>
            <div className="flex gap-3">
              {["ios", "android", "web"].map((p) => (
                <label key={p} className={`px-4 py-2 text-sm rounded-full border cursor-pointer ${form.platforms[p] ? "bg-[#2563EB] border-[#2563EB] text-white" : "bg-white border-[#E2E8F0] text-[#64748B]"}`}>
                  <input type="checkbox" className="hidden" checked={form.platforms[p]} onChange={(e) => setForm({ ...form, platforms: { ...form.platforms, [p]: e.target.checked } })} data-testid={`submit-platform-${p}`} /> {p}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="overline block mb-2 text-[#64748B]">{isNl ? "Regio's" : "Regions"}</label>
            <div className="flex flex-wrap gap-2">
              {REGIONS.map((r) => (
                <button key={r} type="button" onClick={() => toggleArr("regions", r)} className={`px-3 py-1.5 text-xs rounded-full border ${form.regions.includes(r) ? "bg-[#2563EB] border-[#2563EB] text-white" : "bg-white border-[#E2E8F0] text-[#64748B]"}`} data-testid={`submit-region-${r}`}>{r}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="overline block mb-2 text-[#64748B]">{isNl ? "Doelgroepen" : "Target audiences"}</label>
            <div className="flex flex-wrap gap-2">
              {AUDIENCES.map((a) => (
                <button key={a} type="button" onClick={() => toggleArr("target_audience", a)} className={`px-3 py-1.5 text-xs rounded-full border ${form.target_audience.includes(a) ? "bg-[#2563EB] border-[#2563EB] text-white" : "bg-white border-[#E2E8F0] text-[#64748B]"}`} data-testid={`submit-aud-${a}`}>{a}</button>
              ))}
            </div>
          </div>
        </section>

        {/* Step 5: Pricing */}
        <section className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
          <h2 className="font-bold text-xl text-[#0F172A] mb-5">5. {isNl ? "Prijzen" : "Pricing"}</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.pricing.free_tier} onChange={(e) => setForm({ ...form, pricing: { ...form.pricing, free_tier: e.target.checked } })} data-testid="submit-free" /> {isNl ? "Gratis versie beschikbaar" : "Free tier available"}</label>
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.pricing.premium} onChange={(e) => setForm({ ...form, pricing: { ...form.pricing, premium: e.target.checked } })} data-testid="submit-premium" /> {isNl ? "Premium beschikbaar" : "Premium available"}</label>
            <div>
              <label className="overline block mb-1 text-[#64748B]">{isNl ? "Maand (USD)" : "Monthly (USD)"}</label>
              <input type="number" step="0.01" className="hac-input" value={form.pricing.monthly_usd} onChange={(e) => setForm({ ...form, pricing: { ...form.pricing, monthly_usd: parseFloat(e.target.value || "0") } })} data-testid="submit-monthly" />
            </div>
            <div>
              <label className="overline block mb-1 text-[#64748B]">{isNl ? "Jaar (USD)" : "Yearly (USD)"}</label>
              <input type="number" step="0.01" className="hac-input" value={form.pricing.yearly_usd} onChange={(e) => setForm({ ...form, pricing: { ...form.pricing, yearly_usd: parseFloat(e.target.value || "0") } })} data-testid="submit-yearly" />
            </div>
          </div>
        </section>

        {/* Step 6: Features */}
        <section className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
          <h2 className="font-bold text-xl text-[#0F172A] mb-1">6. {isNl ? "Functionaliteiten" : "Functionalities"}</h2>
          <p className="text-sm text-[#64748B] mb-5">{isNl ? `Vink aan wat je app ondersteunt. Geselecteerd: ${form.features.length}.` : `Tick everything your app supports. Selected: ${form.features.length}.`}</p>
          <div className="space-y-3">
            {Object.entries(taxonomy).map(([cat, feats]) => (
              <div key={cat} className="border border-[#E2E8F0] rounded-xl p-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-[#2563EB] mb-2">{cat}</div>
                <div className="flex flex-wrap gap-1.5">
                  {feats.map((f) => (
                    <button key={f} type="button" onClick={() => toggleArr("features", f)} className={`px-2.5 py-1 text-[11px] rounded-full border ${form.features.includes(f) ? "bg-[#EFF6FF] border-[#2563EB] text-[#2563EB]" : "bg-white border-[#E2E8F0] text-[#64748B]"}`} data-testid={`submit-feat-${f}`}>{f}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Step 7: Notes */}
        <section className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
          <h2 className="font-bold text-xl text-[#0F172A] mb-2">7. {isNl ? "Aanvullende notities" : "Additional notes"}</h2>
          <textarea className="hac-input min-h-[100px]" value={submitter.notes} onChange={(e) => setSubmitter({ ...submitter, notes: e.target.value })} placeholder={isNl ? "Iets dat de redactie moet weten? Persberichten, launch-datum, awards…" : "Anything our editors should know? Press kit, launch date, awards…"} data-testid="submit-notes" />
        </section>

        <div className="flex items-center justify-between gap-4">
          <p className="text-xs text-[#94A3B8]">{isNl ? "Door in te dienen ga je akkoord met onze redactionele review." : "By submitting you agree to our editorial review process."}</p>
          <button type="submit" disabled={submitting || uploading} className="hac-btn-primary disabled:opacity-60" data-testid="submit-app-btn">
            <Send size={14} /> {submitting ? (isNl ? "Versturen…" : "Submitting…") : (isNl ? "Aanmelden" : "Submit for review")}
          </button>
        </div>
      </form>
    </main>
  );
};

export default SubmitAppPage;
